import React, { useState } from 'react';
import { EmptyState, ExportBtn, Kpi, PeriodNote, SearchBox, Section, StatusBadge } from './PosBits';
import { PosTable } from './PosTable';
import { inr, monthLabel, num, pctf, usePaged } from './posUtils';

export const MissingTab = ({ params, openCustomer }) => {
  const [scope, setScope] = useState('period');
  const t = usePaged('missing', params, 'start_sales', { scope });
  const d = t.data;
  if (d?.empty) return <EmptyState />;
  const s = d?.summary;
  const sm = monthLabel(d?.start_month);
  const cols = [
    { key: 'customer_id', label: 'Customer ID' },
    { key: 'name', label: 'Name', render: (v) => <b>{v}</b> },
    { key: 'mobile', label: 'Mobile', sortable: false },
    { key: 'classification', label: 'Classification', render: (v, r) => <span className="inline-flex items-center gap-1"><StatusBadge s={r.status} /><span className="text-xs text-slate-500">{v}</span></span> },
    { key: 'last_active_month', label: 'Last Active', render: monthLabel },
    { key: 'months_inactive', label: 'Months Inactive', align: 'right' },
    { key: 'start_orders', label: `${sm} Orders`, align: 'right', render: num },
    { key: 'start_sales', label: `${sm} Sales`, align: 'right', render: inr },
    { key: 'points_earned', label: 'Pts Earned', align: 'right', render: num },
    { key: 'points_burned', label: 'Pts Burned', align: 'right', render: num },
    { key: 'net_points', label: 'Net Pts', align: 'right', render: num },
    { key: 'last_purchase_date', label: 'Last Purchase Date', sortable: false, render: (v) => v || <span className="text-slate-400" title="Not in monthly summary file">N/A</span> },
  ];
  const btn = (v, l) => (
    <button onClick={() => setScope(v)} data-testid={`missing-scope-${v}`}
      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${scope === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
  );
  return (
    <div className="space-y-4" data-testid="missing-tab">
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label={scope === 'period' ? 'Inactive Throughout Period' : 'Not in Ending Month'} value={num(s.count)} tone="text-red-600" testid="missing-kpi-count" />
          <Kpi label="Their Sales Contribution" value={inr(s.lost_sales)} sub={`${pctf(s.pct_of_start_sales)} of ${sm} sales`} testid="missing-kpi-sales" />
          <Kpi label={`% of ${sm} Customers`} value={pctf(s.pct_of_start)} sub={`of ${s.start_customers} customers`} testid="missing-kpi-pct" />
          <Kpi label="Lost (threshold met)" value={num(s.lost_by_threshold)} sub={`Inactive ≥ ${d.settings?.lost_threshold_months} months`} testid="missing-kpi-lost" />
          <Kpi label="Returned in Between" value={num(s.returned_in_between)} sub="absent in ending month but bought in between" testid="missing-kpi-returned" />
        </div>
      )}
      <Section title="Missing / Lost Customers" right={
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex bg-slate-50 border rounded-lg p-0.5">{btn('period', 'Inactive throughout period')}{btn('end_month', 'Not in ending month')}</div>
          <SearchBox value={t.search} onChange={t.setSearch} testid="missing-search" />
          <ExportBtn section="missing" params={{ ...params, scope }} />
        </div>}>
        <div className="mb-2"><PeriodNote data={d} /></div>
        <PosTable columns={cols} rows={d?.items} sort={t.sort} order={t.order} onSort={t.onSort} page={t.page} total={d?.total}
          onPage={t.setPage} loading={t.loading} testid="missing-table" onRowClick={(r) => openCustomer(r.customer_id)} />
        <p className="text-[11px] text-slate-500 mt-2">
          Customers are marked <b>Lost</b> only after {d?.settings?.lost_threshold_months} months without a purchase (change in Points &amp; Settings tab);
          otherwise they are shown as <b>Inactive in Selected Period</b>. Last purchase date is not available in monthly summary files.
        </p>
      </Section>
    </div>
  );
};
